/* eslint-disable react/prop-types */
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import classNames from 'classnames';
import FalconCardHeader from 'components/common/FalconCardHeader';
import LeafletMap from './LeafletMap';
import { toPng } from 'html-to-image';
import cloudDownload from 'assets/img/icons/cloud-download.svg';
import { isEmpty } from 'lodash';
import Model from './Model';
import { FiInfo } from 'react-icons/fi';

const MultiYearMap = ({
  data,
  coordinates,
  legendData,
  type,
  info,
  variant,
  label
}) => {
  const targetElementRef = useRef(null);

  // Sort years in descending order
  const sortYears = useCallback(years => {
    if (!years || !Array.isArray(years)) return [];
    return [...years].sort((a, b) => b - a);
  }, []);

  // Get sorted years from variant
  const getSortedYears = useCallback(() => {
    if (!variant) return [];

    if (Array.isArray(variant)) {
      return sortYears(variant);
    } else if (typeof variant === 'object') {
      return sortYears(Object.keys(variant));
    }
    return [];
  }, [variant, sortYears]);

  const sortedYears = useMemo(() => getSortedYears(), [getSortedYears]);

  // Get available months for a specific year
  const getAvailableMonths = useCallback(
    year => {
      if (!year || typeof variant !== 'object' || !variant[year]) return [];
      return variant[year];
    },
    [variant]
  );

  // Initialize state with safe defaults
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [mapData, setMapData] = useState(null);

  // Initialize selectedYear when sortedYears changes
  useEffect(() => {
    if (sortedYears && sortedYears.length > 0) {
      setSelectedYear(sortedYears[0]);
    }
  }, [sortedYears]);

  // Update selectedMonth when selectedYear changes
  useEffect(() => {
    if (selectedYear) {
      const months = getAvailableMonths(selectedYear);
      if (months && months.length > 0) {
        // Check if current selectedMonth is valid for the new year
        if (months.includes(selectedMonth)) {
          // Keep current month
        } else {
          // Set to first available month
          setSelectedMonth(months[0]);
        }
      } else {
        setSelectedMonth(null);
      }
    }
  }, [selectedYear, getAvailableMonths]);

  // Update mapData when selectedYear or selectedMonth changes
  useEffect(() => {
    if (!data) return;

    const yearMonthKey = selectedMonth
      ? `${selectedYear}::${selectedMonth}`
      : selectedYear;

    if (data[yearMonthKey]) {
      setMapData(data[yearMonthKey]);
    } else {
      // No data for this combination
      setMapData(null);
    }
  }, [data, selectedYear, selectedMonth]);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const handleYearChange = useCallback(event => {
    const newYear = event.target.value;
    setSelectedYear(newYear);
  }, []);

  const handleMonthChange = useCallback(event => {
    setSelectedMonth(event.target.value);
  }, []);

  // Handle image download
  const onButtonClick = useCallback(async () => {
    if (!targetElementRef.current) return;

    const title = label || 'Map';
    const currentDate = new Date();
    const timestamp = currentDate
      .toISOString()
      .replace(/:/g, '-')
      .replace(/\..+/, '');
    const filename = `${title}_${timestamp}.png`;

    // Create timestamp element
    const timestampElement = document.createElement('div');
    timestampElement.textContent = `Downloaded on: ${currentDate.toLocaleString()}`;
    timestampElement.style.position = 'absolute';
    timestampElement.style.bottom = '2px';
    timestampElement.style.right = '10px';
    timestampElement.style.fontSize = '10px';
    timestampElement.style.color = '#666';
    targetElementRef.current.appendChild(timestampElement);

    try {
      const dataUrl = await toPng(targetElementRef.current);
      targetElementRef.current.removeChild(timestampElement);

      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Error generating image:', err);
      if (targetElementRef.current.contains(timestampElement)) {
        targetElementRef.current.removeChild(timestampElement);
      }
    }
  }, [label]);

  // Year/Month selector UI
  const endElement = useMemo(
    () => (
      <Row className="g-1 align-items-center justify-content-end">
        {sortedYears && sortedYears.length > 1 && (
          <Col className="col-auto">
            <Form.Select
              className="ps-2 pe-5 py-0"
              value={selectedYear}
              name="year"
              onChange={handleYearChange}
            >
              {sortedYears.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}

        <Col className="text-center p-0 d-flex align-items-center">
          <span className="m-auto px-2"></span>
        </Col>

        {selectedMonth && getAvailableMonths(selectedYear).length > 0 && (
          <Col className="col-auto">
            <Form.Select
              className="ps-2 pe-5 py-0"
              value={selectedMonth}
              name="month"
              onChange={handleMonthChange}
            >
              {getAvailableMonths(selectedYear).map(month => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}

        <Col className="col-auto">
          <Button
            variant="light"
            size="sm"
            className="border-300 me-1 text-600"
            onClick={onButtonClick}
            disabled={!mapData}
          >
            <img
              src={cloudDownload}
              alt="Download"
              width={15}
              height={'auto'}
            />
          </Button>
        </Col>
      </Row>
    ),
    [
      sortedYears,
      selectedYear,
      selectedMonth,
      handleYearChange,
      handleMonthChange,
      onButtonClick,
      getAvailableMonths
    ]
  );

  // Get data key for selected year/month
  const getSelectedYearMonth = useCallback(() => {
    if (selectedMonth && selectedYear) {
      return `${selectedYear}::${selectedMonth}`;
    }
    return selectedYear;
  }, [selectedYear, selectedMonth]);

  const selectedYearMonth = getSelectedYearMonth();

  return (
    <Card
      ref={targetElementRef}
      className={classNames('h-100', 'overflow-hidden')}
    >
      <FalconCardHeader
        title={label}
        titleTag="h5"
        light={true}
        desc={data && data[0] ? data[0].desc : ''}
        endEl={endElement}
        infoBtn={!isEmpty(info) && <FiInfo onClick={handleShowModal} />}
      />
      <Card.Body className="p-0">
        {mapData ? (
          <LeafletMap
            data={{
              coordinates: coordinates,
              mapData: mapData,
              legendData: legendData,
              type: type,
              label: label
            }}
            className="h-100 bg-white"
            style={{ minHeight: '410px' }}
          />
        ) : (
          <div
            className="d-flex align-items-center justify-content-center bg-light h-100"
            style={{ minHeight: '410px' }}
          >
            <p className="text-muted">
              No map data available for this selection
            </p>
          </div>
        )}
      </Card.Body>
      <Card.Footer className="bg-white py-2 card-footer-style">
        <h5 className="infrence-info">{mapData?.info || ''}</h5>
      </Card.Footer>
      <Model
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        description={info || {}}
        title={label}
      />
    </Card>
  );
};

export default MultiYearMap;
